/**
 * @tyndo/hooks — typed query/mutation hooks for the predictive model registry
 * and retraining state machine.
 *
 * Every path is the real `/api/v1/predictive/*` route the gateway maps (see
 * `Tyndo.Module.Predictive/Endpoints/PredictiveEndpoints.Registry.cs` +
 * `.Retraining.cs`); paths are relative to the `/api/v1` prefix the underlying
 * `useApiQuery` / `useApiMutation` prepend. Predictive-ui views consume these
 * hooks only — no direct fetch. Each mutation invalidates the resource prefix it
 * writes so dependent reads refresh, tenant-scoped via `invalidateApiQueries`.
 * Mirrors the per-module hook convention (LIMS, Materials recalls).
 */

import { useApiQuery, useApiMutation } from './useApi.ts';
import { invalidateApiQueries } from './queryKeys.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantContext } from './contexts.tsx';

import {
  type PredictiveModel,
  type PredictiveListEnvelope,
  type RegisterModelRequest,
  type RetrainingJob,
  type RequestRetrainingRequest,
} from './usePredictiveTypes.ts';

export const PREDICTIVE_MODELS_ENDPOINT = '/predictive/models';
export const PREDICTIVE_RETRAINING_ENDPOINT = '/predictive/retraining';

/**
 * Build an `onSuccess` that invalidates one or more predictive resource
 * prefixes for the active tenant — keeps every mutation's cache-bust scoped +
 * tenant-safe (shared by all four predictive hook modules).
 */
export function useInvalidatePredictive() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenantContext();
  return (...prefixes: string[]) => {
    for (const p of prefixes) {
      invalidateApiQueries(queryClient, tenantId, p);
    }
  };
}

// ── Registry reads ──────────────────────────────────────────────────────────

/** List every predictive model (bare array or provenance envelope). */
export function usePredictiveModels() {
  return useApiQuery<PredictiveModel[] | PredictiveListEnvelope<PredictiveModel>>(
    PREDICTIVE_MODELS_ENDPOINT,
  );
}

// ── Registry writes ─────────────────────────────────────────────────────────

/** Register a new predictive model (POST /predictive/models). */
export function useRegisterPredictiveModel() {
  const invalidate = useInvalidatePredictive();
  return useApiMutation<PredictiveModel, RegisterModelRequest>(
    PREDICTIVE_MODELS_ENDPOINT,
    'POST',
    { onSuccess: () => invalidate(PREDICTIVE_MODELS_ENDPOINT) },
  );
}

/** Deploy a model (POST /predictive/models/{id}/deploy). */
export function useDeployPredictiveModel(modelId: string | null | undefined) {
  const invalidate = useInvalidatePredictive();
  const endpoint = modelId
    ? `${PREDICTIVE_MODELS_ENDPOINT}/${encodeURIComponent(modelId)}/deploy`
    : '';
  return useApiMutation<PredictiveModel, void>(endpoint, 'POST', {
    onSuccess: () => invalidate(PREDICTIVE_MODELS_ENDPOINT),
  });
}

/** Retire a model (POST /predictive/models/{id}/retire). */
export function useRetirePredictiveModel(modelId: string | null | undefined) {
  const invalidate = useInvalidatePredictive();
  const endpoint = modelId
    ? `${PREDICTIVE_MODELS_ENDPOINT}/${encodeURIComponent(modelId)}/retire`
    : '';
  return useApiMutation<PredictiveModel, void>(endpoint, 'POST', {
    onSuccess: () => invalidate(PREDICTIVE_MODELS_ENDPOINT),
  });
}

// ── Retraining reads ────────────────────────────────────────────────────────

/** List retraining jobs (bare array or provenance envelope). */
export function usePredictiveRetrainingJobs() {
  return useApiQuery<RetrainingJob[] | PredictiveListEnvelope<RetrainingJob>>(
    PREDICTIVE_RETRAINING_ENDPOINT,
  );
}

// ── Retraining writes ───────────────────────────────────────────────────────

/** Request a retraining job (POST /predictive/retraining). */
export function useRequestRetraining() {
  const invalidate = useInvalidatePredictive();
  return useApiMutation<RetrainingJob, RequestRetrainingRequest>(
    PREDICTIVE_RETRAINING_ENDPOINT,
    'POST',
    { onSuccess: () => invalidate(PREDICTIVE_RETRAINING_ENDPOINT) },
  );
}

/**
 * Transition a retraining job through its state machine
 * (POST /predictive/retraining/{id}/{verb}) — approve / reject / start /
 * complete / fail / deploy / rollback / cancel. The job id + verb + body all
 * vary per call, so one hook instance drives every transition: call
 * `mutateAsync({ jobId, verb, body })`. On success the retraining resource
 * prefix is invalidated so the job list refreshes.
 */
export interface RetrainingTransitionVariables {
  jobId: string;
  verb: string;
  body?: Record<string, unknown>;
}

export function useTransitionRetrainingJob() {
  const invalidate = useInvalidatePredictive();
  const mutation = useApiMutation<RetrainingJob>();
  const mutateAsync = async (
    vars: RetrainingTransitionVariables,
  ): Promise<RetrainingJob> => {
    const result = await mutation.mutateAsync({
      method: 'POST',
      url: `${PREDICTIVE_RETRAINING_ENDPOINT}/${encodeURIComponent(vars.jobId)}/${vars.verb}`,
      data: vars.body ?? {},
    });
    invalidate(PREDICTIVE_RETRAINING_ENDPOINT);
    return result;
  };
  return { ...mutation, mutateAsync };
}
